import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useAppointments } from '@/hooks/useAppointments';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Calendar, Clock, Phone, Mail, User, ArrowRight, Building2 } from 'lucide-react';
import { format, isPast, isToday, isTomorrow } from 'date-fns';
import { toast } from 'sonner';
import { useProfileSetup } from '@/hooks/useProfileSetup';
import { formatInUserTimezone, getTimezoneAbbreviation } from '@/utils/timezoneUtils';

// Helper to extract contact name from appointment title
const parseContactFromTitle = (title: string | undefined): { firstName?: string; lastName?: string } => {
  if (!title) return {};
  
  // Patterns like "FirstName LastName + Company/Event"
  const match = title.match(/^([^+]+)\s*\+/);
  if (match) {
    const namePart = match[1].trim();
    const parts = namePart.split(' ');
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ')
    };
  }
  
  return {};
};

export function UpcomingAppointments() {
  const { user, isImpersonating, impersonatedUserId } = useAuth();
  // Use impersonated user ID if impersonating, otherwise use current user ID
  const effectiveUserId = isImpersonating && impersonatedUserId ? impersonatedUserId : user?.id;
  const { appointments, loading } = useAppointments(effectiveUserId);
  const { profile } = useProfileSetup();
  const navigate = useNavigate();
  const [startingCall, setStartingCall] = useState<string | null>(null);

  // Filter upcoming appointments (not completed/cancelled) for current user
  // Include draft appointments as they are still active and need to be processed
  const upcomingAppointments = appointments
    .filter(apt => {
      const isUpcoming = ['scheduled', 'confirmed', 'draft'].includes(apt.status);
      const notPast = !isPast(new Date(apt.scheduled_at));
      return isUpcoming && notPast;
    })
    .sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime())
    .slice(0, 5);

  const handleStartCall = async (appointmentId: string, contactId: string | null) => {
    if (!user) return;
    
    setStartingCall(appointmentId);
    
    try {
      // Fetch contact data from GHL
      let contactData: any = null;
      
      if (contactId) {
        const { data: ghlContact } = await supabase
          .from('ghl_contacts')
          .select('*')
          .eq('id', contactId)
          .single();
        
        contactData = ghlContact;
      }

      // Create or find existing quote draft for this appointment
      let query = supabase
        .from('quote_drafts')
        .select('id')
        .eq('sales_rep_id', user.id)
        .eq('deal_status', 'draft')
        .order('created_at', { ascending: false })
        .limit(1);

      if (contactId) {
        query = query.eq('customer_contact_id', contactId);
      } else {
        query = query.is('customer_contact_id', null);
      }

      const { data: existingDraft } = await query.maybeSingle();

      let draftId: string;

      if (existingDraft) {
        // Use existing draft and update with contact data if available
        draftId = existingDraft.id;
        
        // If we have contact data, update the draft with pre-populated intake data
        if (contactData) {
          const customerIntakeData = {
            name: [contactData.first_name, contactData.last_name].filter(Boolean).join(' '),
            email: contactData.email || '',
            phone: contactData.phone || '',
            company_name: contactData.company_name || '',
            lead_source: contactData.lead_source || '',
            service_area: contactData.custom_fields?.service_area || '',
            industry: contactData.custom_fields?.industry || contactData.custom_fields?.business_type || '',
            employee_count: contactData.custom_fields?.employee_count || contactData.custom_fields?.fleet_size || '',
            monthly_call_volume: contactData.custom_fields?.monthly_call_volume || contactData.custom_fields?.call_volume || '',
            role: contactData.custom_fields?.role || contactData.custom_fields?.job_title || '',
            current_solution: contactData.custom_fields?.current_solution || contactData.custom_fields?.current_provider || '',
            pain_points: Array.isArray(contactData.custom_fields?.pain_points) ? contactData.custom_fields.pain_points : [],
            decision_timeline: contactData.custom_fields?.decision_timeline || contactData.custom_fields?.buying_timeline || '',
            budget_authority: contactData.custom_fields?.budget_authority || contactData.custom_fields?.decision_maker || '',
            notes: contactData.custom_fields?.notes || contactData.custom_fields?.comments || '',
          };
          
          await supabase
            .from('quote_drafts')
            .update({ customer_intake_data: customerIntakeData })
            .eq('id', draftId);
        }
        
        // Update appointment link
        await supabase
          .from('appointments')
          .update({ quote_draft_id: draftId })
          .eq('id', appointmentId);
      } else {
        // Create new quote draft with pre-populated customer intake data
        const customerIntakeData = contactData ? {
          name: [contactData.first_name, contactData.last_name].filter(Boolean).join(' '),
          email: contactData.email || '',
          phone: contactData.phone || '',
          company_name: contactData.company_name || '',
          lead_source: contactData.lead_source || '',
          service_area: contactData.custom_fields?.service_area || '',
          industry: contactData.custom_fields?.industry || contactData.custom_fields?.business_type || '',
          employee_count: contactData.custom_fields?.employee_count || contactData.custom_fields?.fleet_size || '',
          monthly_call_volume: contactData.custom_fields?.monthly_call_volume || contactData.custom_fields?.call_volume || '',
          role: contactData.custom_fields?.role || contactData.custom_fields?.job_title || '',
          current_solution: contactData.custom_fields?.current_solution || contactData.custom_fields?.current_provider || '',
          pain_points: Array.isArray(contactData.custom_fields?.pain_points) ? contactData.custom_fields.pain_points : [],
          decision_timeline: contactData.custom_fields?.decision_timeline || contactData.custom_fields?.buying_timeline || '',
          budget_authority: contactData.custom_fields?.budget_authority || contactData.custom_fields?.decision_maker || '',
          notes: contactData.custom_fields?.notes || contactData.custom_fields?.comments || '',
        } : {};

        const { data: newDraft, error } = await supabase
          .from('quote_drafts')
          .insert({
            sales_rep_id: user.id,
            customer_contact_id: contactId,
            current_step: 1,
            customer_intake_data: customerIntakeData,
            deal_status: 'in_progress',
          })
          .select('id')
          .single();

        if (error) throw error;
        
        draftId = newDraft.id;

        // Link appointment to new draft
        await supabase
          .from('appointments')
          .update({ quote_draft_id: draftId })
          .eq('id', appointmentId);
      }

      toast.success('Call prepared! Customer info loaded.');
      navigate(`/configurator?draft=${draftId}`);
    } catch (error: any) {
      console.error('Error starting call:', error);
      toast.error('Failed to prepare call');
    } finally {
      setStartingCall(null);
    }
  };

  const getDateBadge = (scheduledAt: string) => {
    const date = new Date(scheduledAt);
    if (isToday(date)) return <Badge variant="default">Today</Badge>;
    if (isTomorrow(date)) return <Badge variant="secondary">Tomorrow</Badge>;
    return null;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Appointments</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">Loading appointments...</p>
        </CardContent>
      </Card>
    );
  }

  if (upcomingAppointments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Appointments</CardTitle>
          <CardDescription>Your scheduled sales calls</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Calendar className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No upcoming appointments</p>
            <p className="text-sm text-muted-foreground mt-2">
              Booked appointments will appear here
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upcoming Appointments</CardTitle>
        <CardDescription>Your scheduled sales calls</CardDescription>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="space-y-1">
          {upcomingAppointments.map((appointment) => {
            const appointmentData = appointment.appointment_data || {};
            const scheduledDate = new Date(appointment.scheduled_at);
            
            // Parse name from title as fallback
            const parsedFromTitle = parseContactFromTitle(appointmentData.title);
            
            // Extract contact info with multiple fallback sources
            const contactInfo = appointment.ghl_contacts || {
              first_name: appointmentData.contact?.firstName || 
                         appointmentData.contact?.name?.split(' ')[0] ||
                         parsedFromTitle.firstName,
              last_name: appointmentData.contact?.lastName || 
                        appointmentData.contact?.name?.split(' ').slice(1).join(' ') ||
                        parsedFromTitle.lastName,
              email: appointmentData.contact?.email || 
                    appointmentData.contactEmail ||
                    appointmentData.email,
              phone: appointmentData.contact?.phone || 
                    appointmentData.contactPhone ||
                    appointmentData.phone,
              company_name: appointmentData.contact?.companyName || 
                           appointmentData.contact?.businessName ||
                           appointmentData.companyName ||
                           appointmentData.businessName,
            };
            
            const contactName = [contactInfo.first_name, contactInfo.last_name].filter(Boolean).join(' ') || 
                               appointmentData.contact?.name || 
                               appointmentData.title?.split('+')[0]?.trim() ||
                               'No name';

            return (
              <AccordionItem
                key={appointment.id}
                value={appointment.id}
                className="border rounded-lg px-4 hover:bg-accent/5 transition-colors"
              >
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex items-center justify-between w-full pr-4 gap-4">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Date & Time - Compact */}
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="outline" className="gap-1 font-normal">
                          <Calendar className="h-3 w-3" />
                          {formatInUserTimezone(appointment.scheduled_at, 'MMM d', profile?.timezone)}
                        </Badge>
                        <Badge variant="outline" className="gap-1 font-normal">
                          <Clock className="h-3 w-3" />
                          {formatInUserTimezone(appointment.scheduled_at, 'h:mm a', profile?.timezone)}
                        </Badge>
                        {getDateBadge(appointment.scheduled_at)}
                      </div>

                      {/* Contact Info Column */}
                      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                        {/* Contact Name - Prominent */}
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-base truncate">{contactName}</span>
                          {!appointment.ghl_contacts && (
                            <Badge variant="outline" className="text-xs shrink-0">Not synced</Badge>
                          )}
                        </div>
                        
                        {/* Company Name - Secondary */}
                        {contactInfo.company_name && (
                          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                            <Building2 className="h-3.5 w-3.5 shrink-0" />
                            <span className="truncate">{contactInfo.company_name}</span>
                          </div>
                        )}
                        
                        {/* Contact Methods - Inline */}
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          {contactInfo.email && (
                            <a 
                              href={`mailto:${contactInfo.email}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1.5 hover:text-foreground transition-colors"
                            >
                              <Mail className="h-3.5 w-3.5" />
                              <span className="truncate max-w-[180px]">{contactInfo.email}</span>
                            </a>
                          )}
                          {contactInfo.phone && (
                            <a 
                              href={`tel:${contactInfo.phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1.5 hover:text-foreground transition-colors"
                            >
                              <Phone className="h-3.5 w-3.5" />
                              <span>{contactInfo.phone}</span>
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Status Badges */}
                      <div className="flex items-center gap-2 shrink-0">
                        {appointment.deal_closed && (
                          <Badge variant="default" className="bg-green-500">
                            Deal Closed
                          </Badge>
                        )}
                        {appointment.payment_status === 'paid' && (
                          <Badge variant="default" className="bg-blue-500">
                            Paid
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Start Call Button */}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartCall(appointment.id, appointment.contact_id);
                      }}
                      disabled={startingCall === appointment.id}
                      size="sm"
                      className="shrink-0"
                    >
                      {startingCall === appointment.id ? (
                        'Preparing...'
                      ) : (
                        <>
                          Start Call
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </AccordionTrigger>

                <AccordionContent className="pb-3 pt-1">
                  <div className="space-y-2 pl-6">
                    {/* Full Date/Time Details */}
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {formatInUserTimezone(appointment.scheduled_at, 'EEEE, MMMM d, yyyy', profile?.timezone)}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      <span>
                        {formatInUserTimezone(appointment.scheduled_at, 'h:mm a', profile?.timezone)}{' '}
                        {getTimezoneAbbreviation(appointment.scheduled_at, profile?.timezone)}
                      </span>
                      <span>•</span>
                      <span>{appointment.duration_minutes} min</span>
                    </div>

                    {/* Contact Details */}
                    {!appointment.ghl_contacts && (
                      <Badge variant="outline" className="text-xs">
                        Contact not synced
                      </Badge>
                    )}
                    
                    {contactInfo.email && (
                      <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          {contactInfo.email}
                        </span>
                      </div>
                    )}
                    
                    {contactInfo.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <span className="text-muted-foreground">
                          {contactInfo.phone}
                        </span>
                      </div>
                    )}
                    
                    {contactInfo.company_name && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        🏢 {contactInfo.company_name}
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {appointments.length > 5 && (
          <Button
            variant="outline"
            className="w-full"
            onClick={() => navigate('/appointments')}
          >
            View All Appointments
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
